// Add to src/config/googleCalendarApi.js
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth } from "./firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "./firebase";

// Calendar API scope
export const CALENDAR_SCOPES = [
    'https://www.googleapis.com/auth/calendar',
    'https://www.googleapis.com/auth/calendar.events'
];

// Request Google Calendar permissions
export const authorizeGoogleCalendar = async () => {
    try {
        const provider = new GoogleAuthProvider();
        CALENDAR_SCOPES.forEach(scope => provider.addScope(scope));

        const result = await signInWithPopup(auth, provider);
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential.accessToken;

        // Store calendar sync preference - NOTE THE COLLECTION NAME
        await setDoc(doc(db, 'UserInformation', auth.currentUser.uid), {
            googleCalendarSyncEnabled: true,
            googleCalendarToken: token,
        }, { merge: true });

        return token;
    } catch (error) {
        console.error("Calendar authorization error:", error);
        throw error;
    }
};

// Check if user has enabled Google Calendar sync
export const isGoogleCalendarEnabled = async (userId) => {
    try {
        // Change from 'userInformation' to 'UserInformation' to match other functions
        const userPref = await getDoc(doc(db, 'UserInformation', userId));
        if (userPref.exists()) {
            return userPref.data().googleCalendarSyncEnabled || false;
        }
        return false;
    } catch (error) {
        console.error("Error checking calendar preferences:", error);
        return false;
    }
};

// Sync event to Google Calendar
// Enhanced syncEventToGoogleCalendar function
export const syncEventToGoogleCalendar = async (event) => {
    try {
        console.log("Attempting to sync event to Google Calendar:", event);

        // Get user preferences for Google Calendar
        const userPref = await getDoc(doc(db, 'UserInformation', auth.currentUser.uid));
        if (!userPref.exists() || !userPref.data().googleCalendarSyncEnabled) {
            console.log("Google Calendar sync not enabled");
            return null;
        }

        const token = userPref.data().googleCalendarToken;
        if (!token) {
            console.error("No Google Calendar token found");
            return null;
        }

        // Format description with "created by frazza" tag
        const description = event.description ?
            `${event.description}\n\nCreated by frazza` :
            "Created by frazza";

        // Ensure dates are in the correct format
        // Google Calendar API requires RFC3339 timestamps
        const googleEvent = {
            summary: event.title,
            description: description,
            start: {
                dateTime: event.allDay ? undefined : new Date(event.start).toISOString(),
                date: event.allDay ? new Date(event.start).toISOString().split('T')[0] : undefined
            },
            end: {
                dateTime: event.allDay ? undefined : new Date(event.end).toISOString(),
                date: event.allDay ? new Date(event.end).toISOString().split('T')[0] : undefined
            },
            colorId: getGoogleColorId(event.backgroundColor)
        };

        console.log("Formatted Google Calendar event:", googleEvent);

        let url = 'https://www.googleapis.com/calendar/v3/calendars/primary/events';
        let method = 'POST';

        // If event has googleEventId, update instead of create
        if (event.googleEventId) {
            console.log("Updating existing Google Calendar event:", event.googleEventId);
            url = `https://www.googleapis.com/calendar/v3/calendars/primary/events/${event.googleEventId}`;
            method = 'PUT';
        }

        console.log(`Making ${method} request to ${url}`);

        const response = await fetch(url, {
            method,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(googleEvent)
        });

        const responseText = await response.text();
        console.log("Google Calendar API response:", response.status, responseText);

        if (!response.ok) {
            console.error("Google Calendar API error status:", response.status);

            // Handle token expiration
            if (response.status === 401) {
                console.log("Attempting to refresh token due to 401 error");
                // You could call your refreshGoogleToken function here
                return null;
            }

            throw new Error(`Google Calendar API error: Status ${response.status}`);
        }

        const data = JSON.parse(responseText);
        console.log("Successfully synced event to Google Calendar, ID:", data.id);
        return data.id;
    } catch (error) {
        console.error("Error syncing to Google Calendar:", error);
        return null;
    }
};

// Helper function to map your color to Google's colorId
function getGoogleColorId(backgroundColor) {
    // Google Calendar API uses colorIds 1-11
    const colorMap = {
        '#1976D2': '1',  // Blue
        '#4CAF50': '2',  // Green
        '#F44336': '4',  // Red
        '#9C27B0': '3',  // Purple
        '#FF9800': '6',  // Orange
    };
    return colorMap[backgroundColor] || '1'; // Default to blue
}